import {
  Card,
  CardContent,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { type PrefillInspectionQuery } from "../../generated/graphql";

const styles = {
  chipRow: { flexWrap: "wrap" },
} satisfies Record<string, SxProps<Theme>>;

type PrefillInspectionEntity = Extract<
  PrefillInspectionQuery["prefillInspection"],
  { __typename?: "PrefillInspectionEntity" }
>;

/** Verdicts that mean the rule fired; everything else is an abstain. */
const HIT_VERDICTS = new Set(["HIT"]);

const verdictColor = (verdict: string): "success" | "default" =>
  HIT_VERDICTS.has(verdict) ? "success" : "default";

/**
 * Renders one prefill inspection: the sender-deterministic billing-party
 * verdict, the conditional service default for the probed candidate, and the
 * bill-to candidate set. Candidates are clickable to probe their conditional
 * service default.
 */
const PrefillResult = ({
  result,
  selectedBillingPartyContactUuid,
  onSelectCandidate,
  loading,
}: {
  readonly result: PrefillInspectionEntity;
  readonly selectedBillingPartyContactUuid: string | null;
  readonly onSelectCandidate: (uuid: string) => void;
  /** While a probe is in flight, candidates are disabled to stop overlapping probes. */
  readonly loading: boolean;
}) => {
  const { senderBillingParty, conditionalService, candidates } = result;

  return (
    <Stack spacing={2}>
      {!isNil(senderBillingParty) && (
        <Card variant="outlined">
          <CardContent>
            <Typography gutterBottom variant="subtitle1">
              Sender-deterministic billing party
            </Typography>
            <Stack direction="row" spacing={1} sx={styles.chipRow}>
              <Chip
                color={verdictColor(senderBillingParty.verdict)}
                label={senderBillingParty.verdict}
              />
              {!isNil(senderBillingParty.contactUuid) && (
                <Chip
                  variant="outlined"
                  label={`party ${senderBillingParty.contactUuid}`}
                />
              )}
              {!isNil(senderBillingParty.support) && (
                <Chip
                  variant="outlined"
                  label={`support ${senderBillingParty.support}`}
                />
              )}
              {!isNil(senderBillingParty.distinctParties) && (
                <Chip
                  variant="outlined"
                  label={`distinct parties ${senderBillingParty.distinctParties}`}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {!isNil(conditionalService) && (
        <Card variant="outlined">
          <CardContent>
            <Typography gutterBottom variant="subtitle1">
              Conditional service default
            </Typography>
            <Stack direction="row" spacing={1} sx={styles.chipRow}>
              <Chip
                color={verdictColor(conditionalService.verdict)}
                label={conditionalService.verdict}
              />
              {!isNil(conditionalService.serviceId) && (
                <Chip
                  variant="outlined"
                  label={`service ${conditionalService.serviceId}`}
                />
              )}
              {!isNil(conditionalService.support) && (
                <Chip
                  variant="outlined"
                  label={`support ${conditionalService.support}`}
                />
              )}
              {!isNil(conditionalService.share) && (
                <Chip
                  variant="outlined"
                  label={`share ${(conditionalService.share * 100).toFixed(0)}%`}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography gutterBottom variant="subtitle1">
            Billing-party candidates ({candidates.length})
          </Typography>
          {candidates.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No bill-to history in the trailing window.
            </Typography>
          ) : (
            <List dense>
              {candidates.map((candidate) => (
                <ListItemButton
                  key={candidate.uuid}
                  disabled={loading}
                  selected={candidate.uuid === selectedBillingPartyContactUuid}
                  onClick={() => {
                    onSelectCandidate(candidate.uuid);
                  }}
                >
                  <ListItemText
                    primary={candidate.displayName}
                    secondary={candidate.uuid}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default PrefillResult;
